import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseService, Course } from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';

export const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await courseService.getCourses();
        setCourses(data.courses);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load courses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleEnroll = async (courseId: string) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/courses' } } });
      return;
    }

    setEnrollingId(courseId);
    try {
      await courseService.enrollInCourse(courseId);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enroll in course');
    } finally {
      setEnrollingId(null);
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Available Courses</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No courses available yet.</p>
          <p className="text-gray-500 mt-2">Check back soon for new learning content!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              {course.cover_image_url && (
                <img
                  src={course.cover_image_url}
                  alt={course.title}
                  className="w-full h-40 object-cover rounded-md mb-4"
                />
              )}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {course.title}
              </h3>
              <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                {course.short_description || course.description}
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                    course.difficulty_level
                  )}`}
                >
                  {course.difficulty_level.charAt(0).toUpperCase() +
                    course.difficulty_level.slice(1)}
                </span>
                {course.is_free && (
                  <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs font-medium">
                    Free
                  </span>
                )}
                {course.is_featured && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                    Featured
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center mb-4 text-sm text-gray-500">
                <span>{course.estimated_duration_hours} hours</span>
                <span>{course.enrollment_count} enrolled</span>
              </div>
              {course.average_rating > 0 && (
                <div className="flex items-center mb-4 text-sm">
                  <span className="text-yellow-500">★</span>
                  <span className="ml-1 text-gray-700">
                    {course.average_rating.toFixed(1)}
                  </span>
                </div>
              )}
              <button
                onClick={() => handleEnroll(course.id)}
                disabled={enrollingId === course.id}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enrollingId === course.id ? 'Enrolling...' : 'Start Course'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
